import React from "react";
import { useLang } from "../context/LanguageContext";

const TEAKWOOD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4e8b512c-5256-4f3a-807a-6569f93dc661/images/82c3fd6f11cdce9022c1251816418c2695f20eea667d6a98e629a38d8cb35409.png";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1661069543196-9712dc126a91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB0cm9waWNhbCUyMHZpbGxhJTIwbG9tYm9rfGVufDB8fHx8MTc3Njg1MDgwNnww&ixlib=rb-4.1.0&q=85";

const RICE_IMG =
  "https://images.pexels.com/photos/35854210/pexels-photo-35854210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const About = () => {
  const { t } = useLang();
  const pillars = t("about.pillars");

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-ivory text-charcoal"
    >
      {/* WHO WE ARE */}
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 pt-24 sm:pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <p className="overline" data-testid="about-overline">
              — {t("about.overline")}
            </p>
            <h2
              className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              data-testid="about-title"
            >
              {t("about.title")}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p
              className="text-base sm:text-lg leading-[1.8] text-volcanic font-light"
              data-testid="about-body"
            >
              {t("about.body")}
            </p>

            {/* Architectural detail */}
            <div className="mt-12 aspect-[16/9] w-full overflow-hidden bg-sand">
              <img
                src={TEAKWOOD_IMG}
                alt="Tropical teakwood architectural detail"
                className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="hairline mx-auto max-w-[1400px]" />

      {/* WHAT WE DO */}
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="aspect-[4/5] w-full overflow-hidden bg-sand">
              <img
                src={ARCH_IMG}
                alt="Open-air tropical living pavilion"
                className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
              />
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
            <p className="overline">— {t("about.what_overline")}</p>
            <h3 className="mt-6 font-heading font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
              {t("about.what_title")}
            </h3>
            <p className="mt-8 text-base sm:text-lg leading-[1.8] text-volcanic font-light">
              {t("about.what_body")}
            </p>
          </div>
        </div>
      </div>

      {/* Pillars */}
      <div className="bg-sand/60">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
            {Array.isArray(pillars) &&
              pillars.map((p) => (
                <div
                  key={p.k}
                  data-testid={`pillar-${p.k}`}
                  className="group"
                >
                  <div className="font-heading text-6xl text-gold/80 font-light leading-none">
                    {p.k}
                  </div>
                  <div className="mt-6 h-px w-10 bg-charcoal/25 transition-all duration-500 group-hover:w-20 group-hover:bg-charcoal" />
                  <h4 className="mt-6 font-heading text-2xl sm:text-3xl font-normal tracking-tight leading-tight">
                    {p.title}
                  </h4>
                  <p className="mt-4 text-[0.95rem] leading-relaxed text-volcanic font-light">
                    {p.desc}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* MISSION */}
      <div className="relative">
        <div className="absolute inset-0">
          <img
            src={RICE_IMG}
            alt="Rice terraces Lombok"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-charcoal/65 to-charcoal/40" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 sm:px-10 py-28 sm:py-40">
          <div className="max-w-2xl">
            <p className="overline text-ivory/70" data-testid="mission-overline">
              — {t("about.mission_overline")}
            </p>
            <h3
              className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05] tracking-tight"
              data-testid="mission-title"
            >
              {t("about.mission_title")}
            </h3>
            <p className="mt-8 text-base sm:text-lg leading-[1.8] text-ivory/85 font-light">
              {t("about.mission_body")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
